
import { useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useToast } from '@/hooks/use-toast';

interface PDFOptions {
  filename: string;
  elementId: string;
  detailedElementId?: string;
}

export const usePDFGenerator = () => {
  const { toast } = useToast();

  const generatePDF = useCallback( async ( { filename, elementId, detailedElementId }: PDFOptions ) => {
    try {
      toast( {
        title: "Gerando PDF...",
        description: "Por favor, aguarde enquanto o documento é preparado.",
      } );

      const element = document.getElementById( elementId );
      if ( !element ) {
        throw new Error( 'Elemento para PDF não encontrado' );
      }

      // Create PDF instance
      const pdf = new jsPDF( {
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      } );

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Generate first page (summary)
      console.log( 'Generating first page (summary)...' );
      const canvas = await html2canvas( element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
      } );

      const imgData = canvas.toDataURL( 'image/png' );
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min( pdfWidth / imgWidth, pdfHeight / imgHeight );
      const imgX = ( pdfWidth - imgWidth * ratio ) / 2;

      pdf.addImage(
        imgData,
        'PNG',
        imgX,
        0,
        imgWidth * ratio,
        imgHeight * ratio
      );

      // Generate detailed pages if provided
      if ( detailedElementId ) {
        console.log( 'Looking for detailed element:', detailedElementId );
        const detailedElement = document.getElementById( detailedElementId );

        if ( detailedElement ) {
          // Find all segment pages in the detailed content
          const segmentPages = detailedElement.querySelectorAll( '[data-segment]' ) as NodeListOf<HTMLElement>;
          console.log( 'Found segment pages:', segmentPages.length );

          if ( segmentPages.length > 0 ) {
            // Process each segment page individually
            for ( let i = 0; i < segmentPages.length; i++ ) {
              const segmentPage = segmentPages[ i ];
              console.log( `Processing segment page ${ i + 1 }/${ segmentPages.length }` );

              // Add new page
              pdf.addPage();

              // Create canvas for this segment page with optimized settings
              const segmentCanvas = await html2canvas( segmentPage, {
                scale: 1.2, // Reduced scale for better performance
                useCORS: true,
                allowTaint: false,
                backgroundColor: '#ffffff',
                width: 794, // Fixed A4 width in pixels (210mm)
                height: 1123, // Fixed A4 height in pixels (297mm)
                windowWidth: 794,
                windowHeight: 1123,
              } );

              const segmentImgData = segmentCanvas.toDataURL( 'image/png' );

              // Add the segment page to PDF (full page)
              pdf.addImage(
                segmentImgData,
                'PNG',
                0, // No margin - full page
                0, // No margin - full page
                pdfWidth, // Full width
                pdfHeight // Full height
              );
            }

            console.log( `Successfully added ${ segmentPages.length } detailed pages` );
          } else {
            console.warn( 'No segment pages found, falling back to full element capture' );
            // Fallback: capture the entire detailed element
            pdf.addPage();

            const detailedCanvas = await html2canvas( detailedElement, {
              scale: 1.5,
              useCORS: true,
              allowTaint: false,
              backgroundColor: '#ffffff',
            } );

            const detailedImgData = detailedCanvas.toDataURL( 'image/png' );
            const detailedImgWidth = detailedCanvas.width;
            const detailedImgHeight = detailedCanvas.height;
            const detailedRatio = Math.min( pdfWidth / detailedImgWidth, pdfHeight / detailedImgHeight );
            const detailedImgX = ( pdfWidth - detailedImgWidth * detailedRatio ) / 2;

            pdf.addImage(
              detailedImgData,
              'PNG',
              detailedImgX,
              5,
              detailedImgWidth * detailedRatio,
              Math.min( detailedImgHeight * detailedRatio, pdfHeight - 10 )
            );
          }
        } else {
          console.warn( 'Detailed element not found:', detailedElementId );
        }
      }

      // Save the PDF
      console.log( 'Saving PDF:', filename );
      pdf.save( filename );

      // toast({
      //   title: "PDF gerado com sucesso!",
      //   description: "O documento foi baixado com páginas individuais para melhor leitura.",
      // });

      return true;
    } catch ( error ) {
      console.error( 'Error generating PDF:', error );
      toast( {
        title: "Erro ao gerar PDF",
        description: "Ocorreu um erro ao criar o documento. Tente novamente.",
        variant: "destructive",
      } );
      return false;
    }
  }, [ toast ] );

  return { generatePDF };
};
